import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Terminal, Sparkles } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";
import GroupsMultiSelect from "@/components/GroupsMultiSelect";

const EMPTY = {
  command: "", response_type: "text", response_content: "",
  media_url: "", is_active: true, scope: "both",
  group_chat_id: null, group_chat_ids: [], admin_only: false,
};

export default function CommandsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [seeding, setSeeding] = useState(false);

  const fetchItems = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/commands`); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await authAxios.post(`${API}/commands/seed-defaults`);
      alert(`${data.created} comandos creados.`);
      fetchItems();
    } finally { setSeeding(false); }
  };

  const save = async () => {
    if (!form.command.trim()) { alert("Comando requerido"); return; }
    if (editing) await authAxios.put(`${API}/commands/${editing}`, form);
    else await authAxios.post(`${API}/commands`, form);
    setOpen(false); fetchItems();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar comando?")) return;
    await authAxios.delete(`${API}/commands/${id}`); fetchItems();
  };

  const toggle = async (c) => {
    await authAxios.put(`${API}/commands/${c.id}`, { ...c, is_active: !c.is_active });
    fetchItems();
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="commands-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Terminal className="w-7 h-7" /> Comandos del Bot
          </h1>
          <p className="text-slate-500 mt-1">Comandos tipo <code>.menu</code>, <code>.pago</code>, <code>.pedido</code>. Editables en cualquier momento.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDefaults} disabled={seeding} data-testid="cmd-seed-btn">
            <Sparkles className="w-4 h-4 mr-1" /> {seeding ? "Creando..." : "Crear defaults"}
          </Button>
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="cmd-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Comando
          </Button>
        </div>
      </div>

      <Card className="mb-4 bg-amber-50 border-amber-200">
        <CardContent className="p-4 text-sm text-amber-900">
          <strong>Comandos integrados (automáticos):</strong> <code>.catalogo</code> / <code>.stock</code> leen productos,
          <code> .pago</code> lee métodos de pago, <code>.pedido</code> inicia flujo de pedido,
          <code> .humano</code> activa traspaso a admin.
        </CardContent>
      </Card>

      {loading ? <div className="animate-pulse h-40 bg-slate-200 rounded-xl" /> :
       items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          <Terminal className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Aún no hay comandos. Crea los por defecto o agrega uno nuevo.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <Card key={c.id} data-testid={`cmd-card-${c.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <code className="font-mono bg-slate-100 px-2 py-0.5 rounded text-sm font-semibold">{c.command}</code>
                    <Badge variant="secondary" className="text-xs">{c.scope || "both"}</Badge>
                    {c.admin_only && <Badge className="bg-red-100 text-red-800 text-xs">Solo admin</Badge>}
                    {c.response_type !== "text" && <Badge variant="secondary" className="text-xs">{c.response_type}</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">{c.response_content}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch checked={!!c.is_active} onCheckedChange={() => toggle(c)} data-testid={`cmd-toggle-${c.id}`} />
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(c.id); setForm({ ...EMPTY, ...c }); setOpen(true); }} data-testid={`cmd-edit-${c.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(c.id)} data-testid={`cmd-delete-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Comando" : "Nuevo Comando"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Comando *</Label>
              <Input value={form.command} onChange={e => setForm({ ...form, command: e.target.value })} placeholder=".menu" data-testid="cmd-command" />
              <p className="text-xs text-slate-500 mt-1">Se agrega punto inicial automáticamente</p>
            </div>
            <div><Label>Alcance</Label>
              <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="cmd-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Privado y Grupos</SelectItem>
                  <SelectItem value="private">Solo chats privados</SelectItem>
                  <SelectItem value="group">Solo grupos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(form.scope === "group" || form.scope === "both") && (
              <GroupsMultiSelect
                value={form.group_chat_ids || []}
                onChange={(ids) => setForm({ ...form, group_chat_ids: ids })}
                label="¿En qué grupos aplica este comando?"
                helpText="Vacío = aplica en TODOS los grupos donde el bot esté activo."
                testIdPrefix="cmd-groups"
              />
            )}
            <div><Label>Tipo de respuesta</Label>
              <Select value={form.response_type} onValueChange={v => setForm({ ...form, response_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="image">Imagen</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="document">Documento (PDF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Respuesta</Label>
              <Textarea rows={4} value={form.response_content} onChange={e => setForm({ ...form, response_content: e.target.value })} data-testid="cmd-content" />
            </div>
            {form.response_type !== "text" && (
              <div><Label>Archivo / URL del medio</Label>
                <MediaUploadInput
                  value={form.media_url}
                  onChange={(v) => setForm({ ...form, media_url: v })}
                  accept={form.response_type === "image" ? "image/*" :
                          form.response_type === "video" ? "video/*" :
                          form.response_type === "audio" ? "audio/*" :
                          "application/pdf"}
                  testIdPrefix="cmd-media"
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Solo admin (grupos)</Label>
              <Switch checked={!!form.admin_only} onCheckedChange={v => setForm({ ...form, admin_only: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Activo</Label>
              <Switch checked={!!form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="cmd-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
